import { CONTROL_KIND } from '../../../lib/rcu/catalog/control';
import type { ControlKind } from '../../../lib/rcu/catalog/control';
import {
  DIN_LEVEL_PRESETS,
  DIN_VALUE,
  RELAY_VALUE,
  TRAVEL,
  TRAVEL_LABEL,
  toDimmerValue,
  travelValue,
} from '../../../lib/rcu/catalog/dinValue';

/**
 * What a stored number means for one output.
 *
 * A key state holds a list of (output, value) pairs, and the *output* decides
 * what the number is: a relay is on or off, a dimmer is a percentage, a motor is
 * a direction. Keeping that here means the editor, the summary and the encoder
 * all read the same table, and a value can never be interpreted two ways.
 */
export const TARGET_VALUE_KIND = {
  ON_OFF: 'onOff',
  PERCENT: 'percent',
  TRAVEL: 'travel',
} as const;

export type TargetValueKind = (typeof TARGET_VALUE_KIND)[keyof typeof TARGET_VALUE_KIND];

export interface TargetValueSpec {
  readonly label: string;
  /** Which console control edits it; shared with the guest-side catalog. */
  readonly control: ControlKind;
  readonly min: number;
  readonly max: number;
  /** What the editor offers for an output the technician has not set yet. */
  readonly defaultValue: number;
  readonly presets: readonly number[];
  /**
   * The lowest value the hardware acts on, when it is above the minimum: a
   * dimmer told 1–9 runs at 10, so the editor says so.
   */
  readonly floor: number | null;
  /**
   * What "off" means for this kind of output. All three are zero today — a relay
   * has no level, a dimmer at zero is out, and a motor's stop code is zero — but
   * naming it keeps the mirror-as-off action from assuming that.
   */
  readonly offValue: number;
  /** How a value reads in a summary, e.g. `On`, `40%`, `Open`. */
  readonly format: (value: number) => string;
  /** The number the firmware's action string carries. */
  readonly toWire: (value: number) => number;
}

/** A motor's value reads as a direction, whichever way it arrived. */
const travelLabel = (value: number): string => {
  if (value === travelValue(TRAVEL.OPEN)) return TRAVEL_LABEL[TRAVEL.OPEN];
  if (value === travelValue(TRAVEL.CLOSE)) return TRAVEL_LABEL[TRAVEL.CLOSE];
  if (value === travelValue(TRAVEL.STOP)) return TRAVEL_LABEL[TRAVEL.STOP];
  return String(value);
};

export const TARGET_VALUE_SPECS = {
  [TARGET_VALUE_KIND.ON_OFF]: {
    label: 'On or off',
    control: CONTROL_KIND.TOGGLE,
    min: RELAY_VALUE.OFF,
    max: RELAY_VALUE.ON,
    defaultValue: RELAY_VALUE.ON,
    presets: [RELAY_VALUE.OFF, RELAY_VALUE.ON],
    floor: null,
    offValue: RELAY_VALUE.OFF,
    format: (value: number) => (value > RELAY_VALUE.OFF ? 'On' : 'Off'),
    toWire: (value: number) => (value > RELAY_VALUE.OFF ? RELAY_VALUE.ON : RELAY_VALUE.OFF),
  },
  [TARGET_VALUE_KIND.PERCENT]: {
    label: 'Level',
    control: CONTROL_KIND.LEVEL,
    min: DIN_VALUE.MIN,
    max: DIN_VALUE.MAX,
    defaultValue: DIN_VALUE.MAX,
    presets: DIN_LEVEL_PRESETS,
    floor: DIN_VALUE.MIN_LEVEL,
    offValue: DIN_VALUE.MIN,
    format: (value: number) => `${value}%`,
    toWire: toDimmerValue,
  },
  [TARGET_VALUE_KIND.TRAVEL]: {
    label: 'Direction',
    control: CONTROL_KIND.POSITION,
    min: DIN_VALUE.MIN,
    max: DIN_VALUE.INCREASE,
    defaultValue: travelValue(TRAVEL.OPEN),
    presets: [travelValue(TRAVEL.OPEN), travelValue(TRAVEL.STOP), travelValue(TRAVEL.CLOSE)],
    floor: null,
    offValue: travelValue(TRAVEL.STOP),
    format: travelLabel,
    toWire: (value: number) => value,
  },
} as const satisfies Record<TargetValueKind, TargetValueSpec>;

export const TARGET_VALUE_KINDS = [
  TARGET_VALUE_KIND.ON_OFF,
  TARGET_VALUE_KIND.PERCENT,
  TARGET_VALUE_KIND.TRAVEL,
] as const satisfies readonly TargetValueKind[];

/** True when the value can be sent to an output of this kind as it stands. */
export const isValueInRange = (kind: TargetValueKind, value: number): boolean => {
  const spec = TARGET_VALUE_SPECS[kind];
  if (spec.control === CONTROL_KIND.POSITION) {
    // A motor's codes are not a range: only the three directions mean anything.
    return spec.presets.some((preset) => preset === value);
  }
  return Number.isInteger(value) && value >= spec.min && value <= spec.max;
};

/** The value a state should hold for an output of this kind. */
export const defaultValueFor = (kind: TargetValueKind): number =>
  TARGET_VALUE_SPECS[kind].defaultValue;

/** The value that turns this kind of output off. */
export const offValueFor = (kind: TargetValueKind): number => TARGET_VALUE_SPECS[kind].offValue;

/** What a stored value reads as, with the kind's own words. */
export const formatValue = (kind: TargetValueKind, value: number): string =>
  TARGET_VALUE_SPECS[kind].format(value);

/** The number the firmware's action string carries for this value. */
export const valueToWire = (kind: TargetValueKind, value: number): number =>
  TARGET_VALUE_SPECS[kind].toWire(value);

/** The control that edits this kind of value, for the editor to switch on. */
export const controlForValue = (kind: TargetValueKind): ControlKind =>
  TARGET_VALUE_SPECS[kind].control;
